import { CanActivateFn, Router, Routes } from '@angular/router';
import {
  getApiMetrics,
  getEisDashboardBlockchainStatistics,
  getEisDashboardStatistics,
  getEmailCodeResponse,
  getInvoices,
  getPlanTemplates,
  getOperations,
  getPrivacyPolicy,
  getTableSettings,
  getTermsOfUse,
  subscribedWallets
} from './partials/resolvers';
import {
  MonitoringTableSettings,
  tableName as monitoringTableName
} from "./pages/monitoring/components/transactions/components/monitoring-table/monitoring-table.component";
import {
  ReportsTableSettings,
  tableName as reportsTableName
} from "./pages/monitoring/components/reports/components/reports-table/reports-table.component";
import { environment } from "../environments/environment";
import { inject } from "@angular/core";
import { AuthService } from "./partials/services/auth.service";
import { checkQueryParamsGuard } from "./partials/guards/check-query-params.guard";
import { AccountType, CompanyRole } from "./api/api.model";
import { recoveryPartnerGuard } from './partials/guards/recovery-partner-guard';
import { complianceDefaultPageGuard } from './partials/guards/compliance-default-page.guard';
import { eisSignalsAccessGuard } from './partials/guards/eis-signals-access.guard';
import { authSessionGuard } from './partials/guards/auth-session.guard';
import { chooseAccountResetUiGuard } from './partials/guards/choose-account-reset-ui.guard';
import { handleLinkGuard } from './partials/guards/handle-link.guard';
import { EisSignalsTableSettings, tableNameEisSignals } from './pages/eis-signals/components/eis-signals-table/components/eis-signals-table-body/eis-signals-table-body.component';

const withAccountGuard: CanActivateFn = () => {
  const authService: AuthService = inject(AuthService);
  const router: Router = inject(Router);
  return authService.accountDto() ? true : router.createUrlTree(['/login']);
};

const withoutAccountGuard: CanActivateFn = () => {
  const authService: AuthService = inject(AuthService);
  const router: Router = inject(Router);
  return authService.accountDto() ? router.createUrlTree(['/']) : true;
};

const accountGuard = (allowCompanyRoles: CompanyRole[]): CanActivateFn => () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const account = authService.accountDto();
  if (!account) {
    return router.createUrlTree(['/']);
  }

  if (account.accountType === AccountType.PERSONAL) {
    return true;
  }

  if (
    account.accountType === AccountType.CORPORATE &&
    account.companyRole &&
    allowCompanyRoles.includes(account.companyRole)
  ) {
    return true;
  }

  return router.createUrlTree(['/']);
};

const corporateAccountGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const router = inject(Router);

  const account = authService.accountDto();
  return account?.accountType === AccountType.CORPORATE ? true : router.createUrlTree(['/']);
};

const devGuard: CanActivateFn = () => !environment.production && environment.devMode;

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./layout/layout.component').then(({ LayoutComponent }) => LayoutComponent),
    children: [
      {
        path: '',
        canActivate: [withAccountGuard, complianceDefaultPageGuard],
        loadComponent: () => import('./pages/wallets-page/wallets-page.component').then(({ WalletsPageComponent }) => WalletsPageComponent)
      },
      {
        path: 'handle-link',
        canActivate: [handleLinkGuard],
        children: []
      },
      {
        path: 'choose-account',
        canActivate: [authSessionGuard, chooseAccountResetUiGuard],
        loadComponent: () => import('./partials/components/choose-account/choose-account.component').then(({ ChooseAccountComponent }) => ChooseAccountComponent)
      },
      {
        path: 'approve-change-account',
        canActivate: [withAccountGuard],
        loadComponent: () => import('./pages/approve-change-account/approve-change-account.component').then(({ ApproveChangeAccountComponent }) => ApproveChangeAccountComponent)
      },
      {
        path: 'eis-dashboard',
        canActivate: [withAccountGuard, recoveryPartnerGuard, complianceDefaultPageGuard],
        resolve: {
          inputGetEisDashboardBlockchainStatistics: getEisDashboardBlockchainStatistics,
          inputGetEisDashboardStatistics: getEisDashboardStatistics,
          inputGetApiMetrics: getApiMetrics,
        },
        loadComponent: () => import('./pages/eis-dashboard/eis-dashboard.component').then(({ EisDashboardComponent }) => EisDashboardComponent)
      },
      {
        path: 'eis-signals',
        canActivate: [withAccountGuard, eisSignalsAccessGuard],
        resolve: {
          inputGetEisSignalsBlockchainStatistics: getEisDashboardBlockchainStatistics,
          inputGetEisSignalsStatistics: getEisDashboardStatistics,
          inputGetApiMetrics: getApiMetrics,
          transactionTableSettings: getTableSettings<EisSignalsTableSettings>(tableNameEisSignals),
        },
        loadComponent: () => import('./pages/eis-signals/eis-signals.component').then(({ EisSignalsComponent }) => EisSignalsComponent)
      },
      {
        path: 'monitoring',
        canActivate: [withAccountGuard, complianceDefaultPageGuard],
        resolve: {
          transactionTableSettings: getTableSettings<MonitoringTableSettings>(monitoringTableName),
          reportsTableSettings: getTableSettings<ReportsTableSettings>(reportsTableName)
        },
        loadComponent: () => import('./pages/monitoring/monitoring.component').then(({ MonitoringComponent }) => MonitoringComponent)
      },
      {
        path: 'balance',
        canActivate: [withAccountGuard, accountGuard([CompanyRole.OWNER, CompanyRole.HEAD_OF_COMPLIANCE, CompanyRole.AML_OFFICER])],
        resolve: {
          getOperations,
          getInvoices,
          inputPlanTemplates: getPlanTemplates
        },
        loadComponent: () => import('./pages/balance/balance.component').then(({ BalanceComponent }) => BalanceComponent)
      },
      {
        path: 'pricing',
        canActivate: [withAccountGuard, accountGuard([CompanyRole.OWNER, CompanyRole.HEAD_OF_COMPLIANCE, CompanyRole.AML_OFFICER])],
        resolve: {
          inputPlanTemplates: getPlanTemplates
        },
        loadComponent: () => import('./pages/pricing/pricing.component').then(({ PricingComponent }) => PricingComponent)
      },
      {
        path: 'pricing/compare',
        canActivate: [withAccountGuard, accountGuard([CompanyRole.OWNER, CompanyRole.HEAD_OF_COMPLIANCE, CompanyRole.AML_OFFICER])],
        resolve: { 
          inputPlanTemplates: getPlanTemplates
        },
        loadComponent: () => import('./pages/pricing-compare/pricing-compare.component').then(({ PricingCompareComponent }) => PricingCompareComponent)
      },
      {
        path: 'api',
        canActivate: [withAccountGuard, corporateAccountGuard, accountGuard([CompanyRole.OWNER])],
        resolve: {
          getApiMetrics: getApiMetrics,
        },
        loadComponent: () => import('./pages/api/api.component').then(({ ApiComponent }) => ApiComponent)
      },
      {
        path: 'account-upgrade',
        canActivate: [withAccountGuard],
        loadComponent: () => import('./pages/profile/account-upgrade/account-upgrade.component').then(({ AccountUpgradeComponent }) => AccountUpgradeComponent)
      },
      {
        path: 'manage-team-members',
        canActivate: [withAccountGuard, corporateAccountGuard, accountGuard([CompanyRole.OWNER, CompanyRole.HEAD_OF_COMPLIANCE])],
        loadComponent: () => import('./pages/profile/manage-team-members/manage-team-members.component').then(({ ManageTeamMembersComponent }) => ManageTeamMembersComponent)
      },
      {
        path: 'security',
        canActivate: [withAccountGuard, accountGuard([CompanyRole.AML_OFFICER, CompanyRole.VIEWER, CompanyRole.POLICE])],
        loadComponent: () => import('./pages/profile/security/security.component').then(({ SecurityComponent }) => SecurityComponent)
      },
      {
        path: 'privacy-policy',
        resolve: {
          response: getPrivacyPolicy
        },
        loadComponent: () => import('./pages/docs/privacy-policy/privacy-policy.component').then(({ PrivacyPolicyComponent }) => PrivacyPolicyComponent)
      },
      {
        path: 'faq',
        loadComponent: () => import('./pages/docs/faq/faq.component').then(({ FaqComponent }) => FaqComponent)
      },
      {
        path: 'terms-of-service',
        resolve: {
          response: getTermsOfUse
        },
        loadComponent: () => import('./pages/docs/terms-of-service/terms-of-service.component').then(({ TermsOfServiceComponent }) => TermsOfServiceComponent)
      },
      {
        path: 'wiki',
        loadComponent: () => import('./pages/wiki/wiki.component').then(({ WikiComponent }) => WikiComponent)
      },
      {
        path: 'how-eis-works',
        loadComponent: () => import('./pages/how-eis-works/how-eis-works.component').then(({ HowEisWorksComponent }) => HowEisWorksComponent)
      },
      {
        path: 'how-aml-officer-works',
        loadComponent: () => import('./pages/how-aml-officer-works/how-aml-officer-works.component').then(({ HowAmlOfficerWorksComponent }) => HowAmlOfficerWorksComponent)
      },
      {
        path: 'invite-expired',
        loadComponent: () => import('./pages/invite-expired/invite-expired.component').then(({ InviteExpiredComponent }) => InviteExpiredComponent)
      },
      {
        path: '404',
        loadComponent: () => import('./pages/error-pages/404/404.component').then(({ Page404Component }) => Page404Component)
      },
      {
        path: '403',
        loadComponent: () => import('./pages/error-pages/403/403.component').then(({ Page403Component }) => Page403Component)
      },
      {
        path: '401',
        loadComponent: () => import('./pages/error-pages/401/401.component').then(({ Page401Component }) => Page401Component)
      },
      {
        path: 'something-went-wrong',
        loadComponent: () => import('./pages/error-pages/something-went-wrong/something-went-wrong.component').then(({ SomethingWentWrongComponent }) => SomethingWentWrongComponent)
      },
      {
        path: 'access-denied',
        loadComponent: () => import('./pages/error-pages/access-denied/access-denied.component').then(({ AccessDeniedComponent }) => AccessDeniedComponent)
      },
    ]
  },
  {
    path: '',
    canActivate: [withoutAccountGuard],
    children: [
      {
        path: 'login',
        loadComponent: () => import('./pages/auth/login/login.component').then(({ LoginComponent }) => LoginComponent)
      },
      {
        path: 'choose-account-login',
        loadComponent: () => import('./pages/auth/choose-account-login/choose-account-login.component').then(({ ChooseAccountLoginComponent }) => ChooseAccountLoginComponent)
      },
      {
        path: 'password-recovery',
        loadComponent: () => import('./pages/auth/password-recovery/password-recovery.component').then(({ PasswordRecoveryComponent }) => PasswordRecoveryComponent)
      },
      {
        path: 'register-corporate-account',
        loadComponent: () => import('./pages/auth/register-corporate-account/register-corporate-account.component').then(({ RegisterCorporateAccountComponent }) => RegisterCorporateAccountComponent)
      },
      {
        path: 'invitee-login',
        canActivate: [checkQueryParamsGuard(['inviteCode'])],
        resolve: {
          invitationCodeData: getEmailCodeResponse
        },
        loadComponent: () => import('./pages/auth/invitee-login/invitee-login.component').then(({ InviteeLoginComponent }) => InviteeLoginComponent)
      },
    ]
  },
  {
    path: 'invitee-login-has-account',
    canActivate: [checkQueryParamsGuard(['inviteCode'])],
    resolve: {
      invitationCodeData: getEmailCodeResponse
    },
    loadComponent: () => import('./pages/auth/invitee-login-has-account/invitee-login-has-account.component').then(({ InviteeLoginHasAccountComponent }) => InviteeLoginHasAccountComponent)
  },
  {
    path: 'all-svg-icons',
    loadComponent: () => import('./partials/dev/show-all-svg-icons/show-all-svg-icons.component').then(({ ShowAllSvgIconsComponent }) => ShowAllSvgIconsComponent),
    canActivate: [devGuard],
  },
  {
    path: '**',
    redirectTo: '404'
  }
];
